import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  ButtonDropdown,
  SpaceBetween,
  Spinner,
  Input,
  Button,
} from "@amzn/awsui-components-react";
import InfiniteScroll from "react-infinite-scroll-component";
import { useTranslation } from "react-i18next";
import {
  useGetConversationsQuery,
  useSaveTitleMutation,
  useDeleteConversationMutation,
} from "services/atlas-chat";
import { Link } from "react-router-dom";
import styles from "./style.module.scss";
import {
  AtlasChatbotProps,
  GetConversationListRequest,
} from "@amzn/atlas-chat-react-library";
import { GaiaConstants } from "utils/gaia-constants";
import { useNotifications } from "features/notifications/hooks";
import { useAppSelector } from "app/store/hooks";
import CommonDeleteModal from "components/common-delete-modal";

interface ConversationGroupProps {
  title: string;
  conversationListRequest: GetConversationListRequest;
  onDataUpdated: () => void;
  forceRefresh?: number;
}

const ConversationGroup: React.FC<ConversationGroupProps> = ({
  title,
  conversationListRequest,
  onDataUpdated,
  forceRefresh,
}: ConversationGroupProps) => {
  const renameButtonId = "rename";
  const deleteButtonId = "delete";
  const maxScrollHeight = 200;

  const { t } = useTranslation();
  const selectedTeamName =
    localStorage.getItem(GaiaConstants.SELECTED_TEAM_NAME) || "";

  const [saveTitleRequest] = useSaveTitleMutation();
  const [deleteConversation] = useDeleteConversationMutation();
  const { addNotification } = useNotifications();

  const [conversations, setConversations] = useState<
    AtlasChatbotProps.Conversation[]
  >([]);
  const [request, setRequest] = useState<any>(conversationListRequest);
  const [editingConversationId, setEditingConversationId] = useState<
    string | null
  >(null);
  const [editTitle, setEditTitle] = useState<string>("");
  const [isSavingTitle, setIsSavingTitle] = useState<boolean>(false);
  const [isForcingRefresh, setIsForcingRefresh] = useState<boolean>(false);
  const [isDeleting, setIsDeleting] = useState<boolean>(false);
  const [deleteId, setDeleteId] = useState<string>();
  const [showConfirmDeleteModal, setShowConfirmDeleteModal] = useState(false);

  const { data, isLoading, isError, refetch, isFetching } =
    useGetConversationsQuery(request, {
      skip: !request?.appId,
    });

  useEffect(() => {
    if (forceRefresh === 0) {
      return;
    }
    setIsForcingRefresh(true);
    setConversations([]);
    setRequest((prev: any) => ({
      ...prev,
      paginationToken: undefined,
      _timestamp: Date.now(), // force the refresh
    }));
    refetch();
  }, [forceRefresh]);

  useEffect(() => {
    if (data) {
      setConversations((prev: AtlasChatbotProps.Conversation[]) => [
        ...(isForcingRefresh ? [] : prev),
        ...(data.conversations || []),
      ]);
      setIsForcingRefresh(false);
    }
  }, [data]);

  useEffect(() => {
    return () => {
      setConversations([]);
    };
  }, []);

  const saveEditTitle = useCallback(async () => {
    try {
      setIsSavingTitle(true);
      await saveTitleRequest({
        appId: selectedTeamName || "",
        conversationId: editingConversationId!,
        title: editTitle,
      }).unwrap();
      addNotification({
        type: "success",
        header: t("pages.gaia_ai.chatbot.titleSaved"),
        dismissible: true,
        timeout: 5000,
      });
      setEditingConversationId(null);
      setEditTitle("");
      onDataUpdated();
    } catch (e) {
      addNotification({
        type: "error",
        header: t("pages.gaia_ai.chatbot.titleError"),
        dismissible: true,
        timeout: 5000,
      });
    } finally {
      setIsSavingTitle(false);
    }
  }, [editingConversationId, editTitle]);

  const handleShowDelete = async (id: string) => {
    setDeleteId(id);
    setShowConfirmDeleteModal(true);
  };

  const handleConfirmDelete = async (id: string) => {
    try {
      setIsDeleting(true);
      await deleteConversation({
        appId: selectedTeamName,
        conversationId: id,
      }).unwrap();
      onDataUpdated();
      setShowConfirmDeleteModal(false);
    } catch (e) {
      addNotification({
        type: "error",
        header: t("pages.gaia_ai.chatbot.deleteError"),
        dismissible: true,
        timeout: 5000,
      });
    } finally {
      setIsDeleting(false);
      setDeleteId(undefined);
    }
  };

  if (
    ((isLoading || isFetching) && conversations.length === 0) ||
    isSavingTitle ||
    isDeleting
  )
    return (
      <>
        <Box variant="h5">{title}</Box>
        <Spinner />
      </>
    );

  if (isError)
    return (
      <Box variant="p">
        {t("pages.gaia_ai.conversational_history.errorLoading")} {title}.
      </Box>
    );

  if (!isLoading && conversations?.length === 0) {
    return <></>;
  }

  return (
    <SpaceBetween size="xs">
      <Box variant="h5">{title}</Box>

      {conversations?.length > 0 && (
        <SpaceBetween size="xs">
          <InfiniteScroll
            {...(conversations.length >=
            GaiaConstants.ATLAS_CHATBOT_MESSAGES_LIMIT
              ? { height: maxScrollHeight }
              : {})}
            className={styles.infiniteScroll}
            scrollThreshold={0}
            next={() => {
              if (data?.paginationToken) {
                setRequest((prev: any) => ({
                  ...prev,
                  paginationToken: data.paginationToken,
                }));
              }
            }}
            hasMore={!!data?.paginationToken}
            loader={<Spinner />}
            dataLength={conversations.length}
          >
            {conversations.map((conversation, index) => (
              <Box key={`${conversation.conversationId}-${index}`}>
                {editingConversationId === conversation.conversationId ? (
                  <SpaceBetween direction="horizontal" size="xs">
                    <Input
                      value={editTitle}
                      onChange={({ detail }) => setEditTitle(detail.value)}
                      onKeyDown={(event) => {
                        if (event.detail.key === "Enter") {
                          saveEditTitle();
                        }
                      }}
                    />
                    <Button
                      variant="primary"
                      onClick={() => {
                        saveEditTitle();
                      }}
                    >
                      {t("pages.gaia_ai.chatbot.save")}
                    </Button>
                    <Button
                      onClick={() => {
                        setEditingConversationId(null);
                        setEditTitle("");
                      }}
                    >
                      {t("pages.gaia_ai.chatbot.cancel")}
                    </Button>
                  </SpaceBetween>
                ) : (
                  <Link
                    to={`/gaia-ai/chatbot/${conversation.conversationId}`}
                    className={styles.conversationLink}
                  >
                    {conversation.title}
                    <ButtonDropdown
                      items={[
                        {
                          id: renameButtonId,
                          text: t("pages.gaia_ai.chatbot.rename")!,
                        },
                        {
                          id: deleteButtonId,
                          text: t("pages.gaia_ai.chatbot.delete")!,
                        },
                      ]}
                      ariaLabel="Link menu"
                      variant="inline-icon"
                      expandToViewport={true}
                      onItemClick={({ detail }) => {
                        if (detail.id === renameButtonId) {
                          setEditingConversationId(
                            conversation.conversationId!
                          );
                          setEditTitle(conversation.title || "");
                        } else if (detail.id === deleteButtonId) {
                          handleShowDelete(conversation.conversationId);
                        }
                      }}
                    />
                  </Link>
                )}
              </Box>
            ))}
          </InfiniteScroll>
        </SpaceBetween>
      )}

      <CommonDeleteModal
        setVisible={setShowConfirmDeleteModal}
        visible={showConfirmDeleteModal}
        onDelete={handleConfirmDelete}
        items={deleteId}
        title={t("pages.gaia_ai.chatbot.deleteModalTitle")}
        message={t("pages.gaia_ai.chatbot.deleteModalText")}
      />
    </SpaceBetween>
  );
};

export default ConversationGroup;
